// ═══════════════════════════════════════════════════════════════════════════════
// User settings — language, timezone, theme, font size, email notify, account actions
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FONT_SIZE,
  FONT_SIZE_ORDER,
  getLanguageNativeLabel,
  LANGUAGE,
  SUPPORTED_LANGUAGES,
  THEME,
  DEFAULT_USAGE_REGION,
  changePasswordInputSchema,
  type FontSize,
  type Language,
  type Theme,
  type UsageRegion,
} from '@fdh/shared';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatures } from '../../contexts/FeaturesContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { ApiClientError, authApi, settingsApi } from '../../lib/api';
import {
  getBrowserNotificationPermission,
  isWebPushEnabledOnDevice,
  setWebPushEnabledOnDevice,
  shouldShowWebPushSetting,
} from '../../lib/pushNotification';
import {
  ANDROID_CHROME_PLAY_STORE_URL,
  shouldSuggestChromeOnAndroidForPush,
} from '../../lib/deviceDetection';
import { PasswordField } from '../../components/auth/PasswordField';
import { UsageRegionSelect } from '../../components/settings/UsageRegionSelect';
import { AppButton } from '../../components/ui/AppButton';
import { useToast } from '../../components/ui/ToastProvider';

const FONT_SIZE_LABEL_KEY: Record<FontSize, string> = {
  [FONT_SIZE.XSMALL]: 'settings.fontXSmall',
  [FONT_SIZE.SMALL]: 'settings.fontSmall',
  [FONT_SIZE.MEDIUM]: 'settings.fontMedium',
  [FONT_SIZE.LARGE]: 'settings.fontLarge',
  [FONT_SIZE.XLARGE]: 'settings.fontXLarge',
};

const FONT_RANGE_MAX = FONT_SIZE_ORDER.length - 1;

const fontSizeToIndex = (fs: FontSize): number => {
  const i = FONT_SIZE_ORDER.indexOf(fs);
  return i >= 0 ? i : FONT_SIZE_ORDER.indexOf(FONT_SIZE.MEDIUM);
};

const listTimezones = (): string[] => {
  try {
    const intl = Intl as typeof Intl & {
      supportedValuesOf?: (type: 'timeZone') => string[];
    };
    if (typeof intl.supportedValuesOf === 'function') {
      return intl.supportedValuesOf('timeZone');
    }
  } catch {
    // ignore
  }
  return [
    'Asia/Hong_Kong',
    'Asia/Shanghai',
    'Asia/Taipei',
    'Asia/Singapore',
    'UTC',
    'Europe/London',
    'America/New_York',
  ];
};

const isAccountRemovalPasswordIncorrect = (errorMessage: string | undefined): boolean => {
  if (!errorMessage) {
    return false;
  }

  const normalizedMessage = errorMessage.toLowerCase();
  const mentionsPassword =
    normalizedMessage.includes('password') || normalizedMessage.includes('密碼');
  const mentionsIncorrect =
    normalizedMessage.includes('incorrect') || normalizedMessage.includes('錯誤');
  if (!mentionsPassword || !mentionsIncorrect) {
    return false;
  }

  return (
    normalizedMessage.includes('password is incorrect') ||
    normalizedMessage.includes('incorrect password') ||
    normalizedMessage.includes('password incorrect') ||
    normalizedMessage.includes('密碼錯誤')
  );
};

export const SettingsPage = () => {
  const { t, setLanguage } = useLanguage();
  const { showSuccessToast } = useToast();
  const navigate = useNavigate();
  const { user, refreshUser, logout } = useAuth();
  const { translationEnabled } = useFeatures();
  const isGoogleUser = user?.authProvider === 'google';
  const isPaired = Boolean(user?.pairedAt && user.pairedAt.trim() !== '');
  const showBackToPairingStatus = !isPaired || Boolean(user && !user.emailVerified);

  const [nickname, setNickname] = useState('');
  const [language, setLanguageField] = useState<Language>(LANGUAGE.ZH_HANT);
  const [timezone, setTimezone] = useState('Asia/Hong_Kong');
  const [usageRegion, setUsageRegion] = useState<UsageRegion>(DEFAULT_USAGE_REGION);
  const [theme, setTheme] = useState<Theme>(THEME.SYSTEM);
  const [fontSize, setFontSize] = useState<FontSize>(FONT_SIZE.MEDIUM);
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [autoTranslation, setAutoTranslation] = useState(true);
  const [showWebPushSetting, setShowWebPushSetting] = useState(false);
  const [showAndroidChromePushHint, setShowAndroidChromePushHint] = useState(false);
  const [notifyWebPush, setNotifyWebPush] = useState(true);
  const [webPushBusy, setWebPushBusy] = useState(false);
  const [browserNotificationPermission, setBrowserNotificationPermission] =
    useState<NotificationPermission>('default');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [removalBusy, setRemovalBusy] = useState(false);
  const [removalMessage, setRemovalMessage] = useState<string | null>(null);
  const [removalPassword, setRemovalPassword] = useState('');

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [changePasswordSectionExpanded, setChangePasswordSectionExpanded] = useState(false);
  const [accountRemovalSectionExpanded, setAccountRemovalSectionExpanded] = useState(false);

  const [resendBusy, setResendBusy] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);

  const timezones = useMemo(() => listTimezones(), []);

  const timezoneOptions = useMemo(() => {
    if (timezone && !timezones.includes(timezone)) {
      return [timezone, ...timezones];
    }
    return timezones;
  }, [timezones, timezone]);

  useEffect(() => {
    setShowWebPushSetting(shouldShowWebPushSetting());
    setShowAndroidChromePushHint(shouldSuggestChromeOnAndroidForPush());
    setNotifyWebPush(isWebPushEnabledOnDevice());
    setBrowserNotificationPermission(getBrowserNotificationPermission());
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await settingsApi.get();
        if (res.success && res.data) {
          const s = res.data;
          setNickname(user?.nickname ?? '');
          setLanguageField(s.language);
          setTimezone(s.timezone);
          setUsageRegion(s.usageRegion);
          setTheme(s.theme);
          setFontSize(s.fontSize);
          setNotifyEmail(s.notifyEmailActivity !== false);
          setAutoTranslation(s.autoTranslationEnabled !== false);
        } else {
          setError(res.error ?? t('settings.loadError'));
        }
      } catch {
        setError(t('settings.loadError'));
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [t, user?.nickname]);

  const handleSave = async () => {
    setSaving(true);
    setSaveMessage(null);
    setError(null);
    try {
      const res = await settingsApi.update({
        nickname: nickname.trim(),
        language,
        timezone,
        usageRegion,
        theme,
        fontSize,
        notifyEmailActivity: notifyEmail,
        autoTranslationEnabled: autoTranslation,
      });
      if (res.success && res.data) {
        setLanguage(language);
        await refreshUser();
        setSaveMessage(t('settings.saved'));
        showSuccessToast(t('toast.settings.updateSuccess'));
      } else {
        setError(res.error ?? t('settings.saveError'));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t('settings.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleWebPushToggle = async (checked: boolean) => {
    setWebPushBusy(true);
    setError(null);
    try {
      await setWebPushEnabledOnDevice(checked);
      setNotifyWebPush(checked);
    } catch {
      setError(t('settings.webPushToggleError'));
    } finally {
      setWebPushBusy(false);
    }
  };

  const handleFontRange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const idx = Number(e.target.value);
    const next = FONT_SIZE_ORDER[idx] ?? FONT_SIZE.MEDIUM;
    setFontSize(next);
  };

  const handleAccountRemoval = async () => {
    const pwd = removalPassword.trim();
    if (!isGoogleUser && !pwd) {
      setError(t('settings.accountRemovalPasswordLabel'));
      return;
    }
    setRemovalBusy(true);
    setRemovalMessage(null);
    setError(null);
    try {
      const res = await settingsApi.requestAccountRemoval(
        isGoogleUser ? {} : { password: pwd }
      );
      if (res.success) {
        setRemovalPassword('');
        if (!isGoogleUser) {
          showSuccessToast(t('settings.accountRemovalDeleted'));
          await logout();
          navigate('/', { replace: true });
          return;
        }
        setRemovalMessage(t('settings.accountRemovalSuccess'));
      } else {
        if (isAccountRemovalPasswordIncorrect(res.error)) {
          setError(t('settings.accountRemovalPasswordIncorrect'));
          return;
        }
        setError(res.error ?? t('settings.accountRemovalError'));
      }
    } catch (accountRemovalError) {
      const apiClientError = accountRemovalError instanceof ApiClientError ? accountRemovalError : null;
      const errorBodyMessage =
        apiClientError && typeof apiClientError.body.error === 'string'
          ? apiClientError.body.error
          : undefined;
      const rawErrorMessage =
        accountRemovalError instanceof Error ? accountRemovalError.message : undefined;
      if (
        (apiClientError?.status === 401 && !isGoogleUser) ||
        isAccountRemovalPasswordIncorrect(errorBodyMessage) ||
        isAccountRemovalPasswordIncorrect(apiClientError?.message) ||
        isAccountRemovalPasswordIncorrect(rawErrorMessage)
      ) {
        setError(t('settings.accountRemovalPasswordIncorrect'));
      } else {
        setError(t('settings.accountRemovalError'));
      }
    } finally {
      setRemovalBusy(false);
    }
  };

  const handleChangePassword = async () => {
    const parsed = changePasswordInputSchema.safeParse({
      currentPassword,
      newPassword,
      confirmNewPassword,
    });
    if (!parsed.success) {
      setError(parsed.error.issues.map((i) => i.message).join(', '));
      return;
    }
    setPasswordBusy(true);
    setError(null);
    try {
      const res = await authApi.changePassword(parsed.data);
      if (res.success) {
        showSuccessToast(t('toast.settings.changePasswordSuccess'));
        await logout();
        navigate('/', { replace: true });
        return;
      }
      setError(res.error ?? t('settings.changePasswordError'));
    } catch (e) {
      if (
        e instanceof ApiClientError &&
        (e.code === 'CURRENT_PASSWORD_INCORRECT' || e.status === 401)
      ) {
        setError(t('settings.changePasswordCurrentPasswordIncorrect'));
        return;
      }
      setError(e instanceof Error ? e.message : t('settings.changePasswordError'));
    } finally {
      setPasswordBusy(false);
    }
  };

  const handleResendVerification = async () => {
    setResendBusy(true);
    setResendMessage(null);
    setError(null);
    try {
      const res = await authApi.resendVerification();
      if (res.success) {
        setResendMessage(t('settings.resendVerificationSuccess'));
      } else {
        setError(res.error ?? t('settings.resendVerificationError'));
      }
    } catch (e) {
      if (
        e instanceof ApiClientError &&
        e.status === 429 &&
        e.code === 'RESEND_VERIFICATION_COOLDOWN'
      ) {
        setError(t('settings.resendVerificationCooldown'));
        return;
      }
      setError(e instanceof Error ? e.message : t('settings.resendVerificationError'));
    } finally {
      setResendBusy(false);
    }
  };

  const handleKeySave = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      void handleSave();
    }
  };

  const handleToggleChangePasswordSection = () => {
    setChangePasswordSectionExpanded((prev) => !prev);
  };

  const handleToggleAccountRemovalSection = () => {
    setAccountRemovalSectionExpanded((prev) => !prev);
  };

  return (
    <div className="min-h-full bg-gray-50 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
      <header className="z-10 bg-white shadow-sm dark:bg-gray-900 dark:shadow-black/30">
        <div className="mx-auto max-w-lg px-4 py-4">
          <div className="flex items-start gap-3">
            {showBackToPairingStatus ? (
              <button
                type="button"
                onClick={() => navigate('/pairing/status')}
                aria-label={t('settings.backToPairingStatus')}
                className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-200 dark:hover:bg-gray-800"
              >
                <span aria-hidden>&lt;</span>
              </button>
            ) : null}
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">{t('settings.title')}</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('settings.subtitle')}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-lg px-4 py-6 pb-24">
        {loading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('common.loading')}</p>
        ) : null}

        {error ? (
          <p className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
            {error}
          </p>
        ) : null}

        {saveMessage ? (
          <p className="mb-4 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800" role="status">
            {saveMessage}
          </p>
        ) : null}

        {user && !user.emailVerified ? (
          <div
            className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4"
            role="region"
            aria-label={t('settings.emailUnverifiedBanner')}
          >
            <p className="text-sm text-amber-900">{t('settings.emailUnverifiedBanner')}</p>
            <button
              type="button"
              disabled={resendBusy}
              onClick={() => void handleResendVerification()}
              className="mt-3 w-full min-h-11 rounded-xl bg-amber-700 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-amber-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 disabled:cursor-not-allowed disabled:opacity-60"
              aria-label={t('settings.resendVerification')}
              tabIndex={0}
            >
              {resendBusy ? t('settings.resendVerificationSending') : t('settings.resendVerification')}
            </button>
            {resendMessage ? (
              <p className="mt-2 text-sm text-emerald-800" role="status">
                {resendMessage}
              </p>
            ) : null}
          </div>
        ) : null}

        <section
          className="space-y-6 rounded-xl bg-white p-4 shadow-sm dark:bg-gray-900 dark:shadow-black/30"
          aria-labelledby="settings-form-heading"
        >
          <h2 id="settings-form-heading" className="sr-only">
            {t('settings.title')}
          </h2>

          <div>
            <label htmlFor="settings-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('auth.emailLabel')}
            </label>
            <input
              id="settings-email"
              type="email"
              value={user?.email ?? ''}
              readOnly
              disabled
              className="mt-1 w-full cursor-not-allowed rounded-lg border border-gray-200 bg-gray-100 px-3 py-2 text-gray-600 shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
            />
          </div>

          <div>
            <label htmlFor="settings-nickname" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('settings.nicknameLabel')}
            </label>
            <input
              id="settings-nickname"
              type="text"
              value={nickname}
              maxLength={50}
              onChange={(e) => setNickname(e.target.value)}
              className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              placeholder={t('settings.nicknamePlaceholder')}
              autoComplete="nickname"
            />
          </div>

          <div>
            <label htmlFor="settings-language" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('settings.languageLabel')}
            </label>
            <select
              id="settings-language"
              value={language}
              onChange={(e) => setLanguageField(e.target.value as Language)}
              className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
            >
              {SUPPORTED_LANGUAGES.map((lang) => (
                <option key={lang} value={lang}>
                  {getLanguageNativeLabel(lang)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="settings-timezone" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('settings.timezoneLabel')}
            </label>
            <select
              id="settings-timezone"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="mt-1 max-h-48 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
            >
              {timezoneOptions.map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </select>
          </div>

          <UsageRegionSelect
            id="settings-usage-region"
            value={usageRegion}
            onChange={setUsageRegion}
            label={t('settings.usageRegionLabel')}
            variant="settings"
          />

          <fieldset>
            <legend className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('settings.themeLabel')}</legend>
            <div className="mt-2 flex flex-wrap gap-2">
              {(
                [
                  [THEME.LIGHT, t('settings.themeLight')],
                  [THEME.DARK, t('settings.themeDark')],
                  [THEME.SYSTEM, t('settings.themeSystem')],
                ] as const
              ).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setTheme(value)}
                  className={`rounded-lg border px-3 py-2 text-sm font-medium transition focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    theme === value
                      ? 'border-primary-600 bg-primary-50 text-primary-800'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </fieldset>

          <div>
            <label htmlFor="settings-font-range" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('settings.fontSizeLabel')}:{' '}
              {t(FONT_SIZE_LABEL_KEY[fontSize] ?? 'settings.fontMedium')}
            </label>
            <input
              id="settings-font-range"
              type="range"
              min={0}
              max={FONT_RANGE_MAX}
              step={1}
              value={fontSizeToIndex(fontSize)}
              onChange={handleFontRange}
              className="mt-3 w-full accent-primary-600"
              aria-valuemin={0}
              aria-valuemax={FONT_RANGE_MAX}
              aria-valuenow={fontSizeToIndex(fontSize)}
              aria-valuetext={fontSize}
            />
            <div
              className="mt-1 grid w-full grid-cols-5 items-start gap-x-0 text-[0.625rem] leading-snug text-gray-500 dark:text-gray-400 sm:gap-x-1 sm:text-xs sm:leading-normal"
              aria-hidden
            >
              {FONT_SIZE_ORDER.map((fs, idx) => {
                const isFirst = idx === 0;
                const isLast = idx === FONT_SIZE_ORDER.length - 1;
                return (
                  <span
                    key={fs}
                    className={`min-w-0 max-w-full break-words px-0.5 ${
                      isFirst ? 'text-left' : isLast ? 'text-right' : 'text-center'
                    }`}
                  >
                    {t(FONT_SIZE_LABEL_KEY[fs])}
                  </span>
                );
              })}
            </div>
          </div>

          <div className="flex items-start gap-3">
            <input
              id="settings-notify-email"
              type="checkbox"
              checked={notifyEmail}
              onChange={(e) => setNotifyEmail(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <label htmlFor="settings-notify-email" className="text-sm text-gray-700 dark:text-gray-300">
              {t('settings.notifyEmailLabel')}
            </label>
          </div>

          {translationEnabled ? (
            <div className="flex items-start gap-3">
              <input
                id="settings-auto-translation"
                type="checkbox"
                checked={autoTranslation}
                onChange={(e) => setAutoTranslation(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <div>
                <label
                  htmlFor="settings-auto-translation"
                  className="text-sm text-gray-700 dark:text-gray-300"
                >
                  {t('settings.autoTranslationLabel')}
                </label>
                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                  {t('settings.autoTranslationHint')}
                </p>
              </div>
            </div>
          ) : null}

          {showAndroidChromePushHint ? (
            <div
              className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 dark:border-amber-800/60 dark:bg-amber-950/40"
              role="region"
              aria-label={t('settings.androidChromePushHintTitle')}
            >
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {t('settings.androidChromePushHintTitle')}
              </p>
              <p className="mt-1 text-xs text-gray-700 dark:text-gray-300">
                {t('settings.androidChromePushHintBody')}
              </p>
              <AppButton
                type="button"
                variant="primary"
                className="mt-3 min-h-9 px-3 py-1.5 text-xs"
                aria-label={t('settings.androidChromePushHintCta')}
                onClick={() => {
                  window.open(ANDROID_CHROME_PLAY_STORE_URL, '_blank', 'noopener,noreferrer');
                }}
              >
                {t('settings.androidChromePushHintCta')}
              </AppButton>
            </div>
          ) : null}

          {showWebPushSetting ? (
            <div>
              <div className="flex items-start gap-3">
                <input
                  id="settings-notify-web-push"
                  type="checkbox"
                  checked={notifyWebPush}
                  disabled={webPushBusy}
                  onChange={(e) => void handleWebPushToggle(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-60"
                />
                <label htmlFor="settings-notify-web-push" className="text-sm text-gray-700 dark:text-gray-300">
                  {t('settings.notifyWebPushLabel')}
                </label>
              </div>
              {browserNotificationPermission === 'denied' ? (
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400" role="note">
                  {t('settings.notifyWebPushPermissionDeniedHint')}
                </p>
              ) : null}
            </div>
          ) : null}

          <AppButton
            type="button"
            variant="primary"
            fullWidth
            onClick={() => void handleSave()}
            onKeyDown={handleKeySave}
            disabled={saving || loading}
            className="shadow-sm"
          >
            {saving ? t('settings.saving') : t('settings.save')}
          </AppButton>

        </section>

        {!isGoogleUser ? (
          <section
            className="mt-6 rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900 dark:shadow-black/30"
            aria-labelledby="settings-password-heading"
          >
            <button
              type="button"
              id="settings-password-heading"
              aria-expanded={changePasswordSectionExpanded}
              aria-controls="settings-password-panel"
              aria-label={
                changePasswordSectionExpanded
                  ? t('settings.collapseChangePasswordSectionAria')
                  : t('settings.expandChangePasswordSectionAria')
              }
              onClick={handleToggleChangePasswordSection}
              className="flex w-full items-center justify-between gap-3 rounded-lg py-1 text-left focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <span className="text-base font-semibold text-gray-900 dark:text-gray-100">
                {t('settings.changePassword')}
              </span>
              <svg
                className={`h-5 w-5 shrink-0 text-gray-500 transition-transform dark:text-gray-400 ${
                  changePasswordSectionExpanded ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div
              id="settings-password-panel"
              role="region"
              aria-labelledby="settings-password-heading"
              hidden={!changePasswordSectionExpanded}
              className="mt-4 space-y-4"
            >
              <p className="text-xs text-gray-500 dark:text-gray-400">{t('settings.changePasswordHint')}</p>
              <PasswordField
                id="settings-current-password"
                name="currentPassword"
                label={t('settings.currentPasswordLabel')}
                value={currentPassword}
                onChange={setCurrentPassword}
                autoComplete="current-password"
                disabled={passwordBusy}
                showLabels={{ show: t('auth.showPassword'), hide: t('auth.hidePassword') }}
              />
              <PasswordField
                id="settings-new-password"
                name="newPassword"
                label={t('settings.newPasswordLabel')}
                value={newPassword}
                onChange={setNewPassword}
                autoComplete="new-password"
                disabled={passwordBusy}
                showLabels={{ show: t('auth.showPassword'), hide: t('auth.hidePassword') }}
                aria-describedby="settings-new-password-hint"
              />
              <p id="settings-new-password-hint" className="text-xs text-gray-500 dark:text-gray-400">
                {t('auth.passwordRulesHint')}
              </p>
              <PasswordField
                id="settings-confirm-new-password"
                name="confirmNewPassword"
                label={t('settings.confirmNewPasswordLabel')}
                value={confirmNewPassword}
                onChange={setConfirmNewPassword}
                autoComplete="new-password"
                disabled={passwordBusy}
                showLabels={{ show: t('auth.showPassword'), hide: t('auth.hidePassword') }}
              />
              <AppButton
                type="button"
                variant="primary"
                fullWidth
                disabled={
                  passwordBusy ||
                  !currentPassword.trim() ||
                  !newPassword.trim() ||
                  !confirmNewPassword.trim()
                }
                onClick={() => void handleChangePassword()}
                className="shadow-sm"
              >
                {passwordBusy ? t('settings.changePasswordSaving') : t('settings.changePasswordSubmit')}
              </AppButton>
            </div>
          </section>
        ) : null}

        <section
          className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-700/50 dark:bg-amber-900/20"
          aria-labelledby="settings-account-removal-heading"
        >
          <button
            type="button"
            id="settings-account-removal-heading"
            aria-expanded={accountRemovalSectionExpanded}
            aria-controls="settings-account-removal-panel"
            aria-label={
              accountRemovalSectionExpanded
                ? t('settings.collapseAccountRemovalSectionAria')
                : t('settings.expandAccountRemovalSectionAria')
            }
            onClick={handleToggleAccountRemovalSection}
            className="flex w-full items-center justify-between gap-3 rounded-lg py-1 text-left focus:outline-none focus:ring-2 focus:ring-amber-500"
          >
            <span className="text-sm font-semibold text-amber-900 dark:text-amber-200">
              {t('settings.accountRemoval')}
            </span>
            <svg
              className={`h-5 w-5 shrink-0 text-amber-800 transition-transform dark:text-amber-300/90 ${
                accountRemovalSectionExpanded ? 'rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <div
            id="settings-account-removal-panel"
            role="region"
            aria-labelledby="settings-account-removal-heading"
            hidden={!accountRemovalSectionExpanded}
            className="mt-3 space-y-3"
          >
            {!isGoogleUser ? (
              <>
                <p className="text-xs text-amber-900/90 dark:text-amber-200/90">
                  {t('settings.accountRemovalHint')}
                </p>
                <PasswordField
                  id="settings-removal-password"
                  name="removalPassword"
                  label={t('settings.accountRemovalPasswordLabel')}
                  value={removalPassword}
                  onChange={setRemovalPassword}
                  autoComplete="current-password"
                  disabled={removalBusy}
                  showLabels={{ show: t('auth.showPassword'), hide: t('auth.hidePassword') }}
                />
              </>
            ) : (
              <p className="text-xs text-amber-900/90 dark:text-amber-200/90">{t('settings.accountRemovalGoogleHint')}</p>
            )}
            {removalMessage ? (
              <p className="text-sm text-emerald-800 dark:text-emerald-300/90" role="status">
                {removalMessage}
              </p>
            ) : null}
            <AppButton
              type="button"
              variant="danger"
              fullWidth
              onClick={() => void handleAccountRemoval()}
              disabled={removalBusy || (!isGoogleUser && !removalPassword.trim())}
              aria-label={t('settings.accountRemovalSubmit')}
            >
              {removalBusy ? t('common.loading') : t('settings.accountRemovalSubmit')}
            </AppButton>
          </div>
        </section>
      </main>
    </div>
  );
};
